import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useCalendarsForTables } from "../hooks/calendars/useCalendarsForTables";
import { useCreateCalendar } from "../hooks/calendars/useCreateCalendar";
import { useBatchUpsertCalendarDates } from "../hooks/calendars/calendarDates/useBatchUpsertCalendarDates";
import { useCalendar } from "../hooks/calendars/useCalendar";
import useCalendarDates from "../hooks/calendars/calendarDates/useCalendarDates";

const CalendarContext = createContext(null);

export function CalendarProvider({ children }) {
  const [calendarId, setCalendarId] = useState("");
  const [enabledTableFetch, setEnabledTableFetch] = useState(false);
  const [tableFilters, setTableFilters] = useState({ search: "", isActive: true, type: "", localAuthorityId: "" });

  const singleFetch = useCalendar(calendarId);
  const calendarDatesFetch = useCalendarDates(calendarId);
  const tableFetch = useCalendarsForTables({ filters: tableFilters, enabled: enabledTableFetch });
  const createCalendar = useCreateCalendar();
  const upsertCalendarDates = useBatchUpsertCalendarDates();

  const enableTableFetch = useCallback(() => {
    setEnabledTableFetch(true);
  }, []);

  const updateTableFilters = useCallback((patch) => {
    setTableFilters((prev) => {
      return typeof patch === "function" ? patch(prev) : { ...prev, ...patch };
    });
  }, []);

  const value = useMemo(
    () => ({
      singleFetch: {
        data: singleFetch.data,
        isLoading: singleFetch.isLoading,
        isPending: singleFetch.isPending,
        error: singleFetch.error,
        calendarId,
        setCalendarId,
      },
      calendarDatesFetch: {
        data: calendarDatesFetch.data,
        isLoading: calendarDatesFetch.isLoading,
        isPending: calendarDatesFetch.isPending,
        error: calendarDatesFetch.error,
      },
      tableFetch: {
        data: tableFetch.data,
        isLoading: tableFetch.isLoading,
        error: tableFetch.error,
        fetchNextPage: tableFetch.fetchNextPage,
        isFetchingNextPage: tableFetch.isFetchingNextPage,
        refetch: tableFetch.refetch,
        isRefetching: tableFetch.isRefetching,
        enableTableFetch,
        tableFilters,
        updateTableFilters,
      },
      createCalendar: {
        mutateAsync: createCalendar.mutateAsync,
        isPending: createCalendar.isPending,
        error: createCalendar.error,
        isSuccess: createCalendar.isSuccess,
        status: createCalendar.status,
      },
      upsertCalendarDates: {
        mutateAsync: upsertCalendarDates.mutateAsync,
        data: upsertCalendarDates.data,
        isPending: upsertCalendarDates.isPending,
        error: upsertCalendarDates.error,
        isSuccess: upsertCalendarDates.isSuccess,
        status: upsertCalendarDates.status,
      },
    }),
    [
      singleFetch.data,
      singleFetch.isLoading,
      singleFetch.isPending,
      singleFetch.error,
      calendarId,
      setCalendarId,
      calendarDatesFetch.data,
      calendarDatesFetch.isLoading,
      calendarDatesFetch.isPending,
      calendarDatesFetch.error,
      tableFetch.data,
      tableFetch.isLoading,
      tableFetch.error,
      tableFetch.fetchNextPage,
      tableFetch.isFetchingNextPage,
      tableFetch.refetch,
      tableFetch.isRefetching,
      enableTableFetch,
      tableFilters,
      updateTableFilters,
      createCalendar.mutateAsync,
      createCalendar.isPending,
      createCalendar.error,
      createCalendar.isSuccess,
      createCalendar.status,
      upsertCalendarDates.mutateAsync,
      upsertCalendarDates.data,
      upsertCalendarDates.isPending,
      upsertCalendarDates.error,
      upsertCalendarDates.isSuccess,
      upsertCalendarDates.status,
    ]
  );

  return <CalendarContext.Provider value={value}>{children}</CalendarContext.Provider>;
}

export function useCalendarContext() {
  const context = useContext(CalendarContext);

  if (!context) {
    throw new Error("useCalendarContext must be used inside CalendarContext");
  }

  return context;
}
